import { supabase } from '@/core/database/supabase';
import type { UserNewFormData } from '../types/userNew.types';

// ─── Auto-gen user_code chuẩn LANDSCAPE: USR-XXXXX ───────────────────────────
async function generateUserCode(companyId: string): Promise<string> {
  const { count } = await supabase
    .from('user_profiles')
    .select('*', { count: 'exact', head: true })
    .eq('company_id', companyId);

  const next = (count || 0) + 1;
  const base = `USR-${String(next).padStart(5, '0')}`;

  // Kiểm tra trùng, thêm hậu tố nếu cần
  const { data: existing } = await supabase
    .from('user_profiles')
    .select('user_code')
    .eq('user_code', base)
    .maybeSingle();

  if (existing) {
    return `USR-${String(next).padStart(5, '0')}-${Math.random().toString(36).slice(2, 5).toUpperCase()}`;
  }
  return base;
}

// ─── Upload avatar – path chuẩn LANDSCAPE ─────────────────────────────────────
async function uploadAvatar(file: File, companyId: string, userId: string): Promise<string> {
  // Chuẩn hóa tên file, ưu tiên .webp
  const ext = file.name.split('.').pop() || 'jpg';
  const fileName = `${userId}.${ext}`;
  const filePath = `media-assets/${companyId}/users/${fileName}`;

  const { error } = await supabase.storage
    .from('media-assets')
    .upload(filePath, file, { upsert: true });

  if (error) throw error;

  const { data: { publicUrl } } = supabase.storage
    .from('media-assets')
    .getPublicUrl(filePath);

  return publicUrl;
}

// ─── Service chính ────────────────────────────────────────────────────────────
export const UserNewService = {
  async createUser(data: UserNewFormData, companyId: string) {
    // 1. Auto-gen user_code
    const userCode = await generateUserCode(companyId);

    // 2. Insert profile (avatar_path để null trước, upload sau)
    const { data: profile, error } = await supabase
      .from('user_profiles')
      .insert([{
        // --- BẮT BUỘC ---
        company_id: companyId,
        user_code: userCode,
        full_name: data.full_name,
        role_code: data.role_code || 'pm',

        // --- Thông tin cá nhân ---
        email: data.email || null,
        phone: data.phone || null,
        gender: data.gender || null,
        birthday: data.birthday || null,
        birth_year: data.birth_year ? Number(data.birth_year) : null,
        id_number: data.id_number || null,
        id_issue_date: data.id_issue_date || null,
        id_issue_place: data.id_issue_place || null,
        nationality: data.nationality || 'Việt Nam',
        marital_status: data.marital_status || null,
        permanent_address: data.permanent_address || null,
        current_address: data.current_address || null,
        avatar_path: null, // upload sau khi có ID

        // --- Tài khoản ---
        username: data.username || null,
        account_email: data.account_email || data.email || null,

        // --- Công việc ---
        job_title: data.job_title || null,
        direct_manager_id: data.direct_manager_id || null,
        join_date: data.join_date || null,
        employment_type: data.employment_type || null,
        is_active: data.status === 'active',
        recruitment_source: data.recruitment_source || null,
        notes: data.notes || null,

        // --- Học vấn ---
        education_level: data.education_level || null,
        university: data.university || null,
        major: data.major || null,
        graduation_year: data.graduation_year ? Number(data.graduation_year) : null,
        skills: data.skills || [],

        // --- Liên hệ khẩn cấp ---
        emergency_contact_name: data.emergency_contact_name || null,
        emergency_contact_phone: data.emergency_contact_phone || null,
        emergency_contact_relationship: data.emergency_contact_relationship || null,
      }])
      .select()
      .single();

    if (error) throw error;

    // 3. Upload avatar sau khi có profile.id (Atomic Media – Preview-first)
    if (data._avatarFile && profile) {
      try {
        const avatarUrl = await uploadAvatar(data._avatarFile, companyId, profile.id);
        await supabase
          .from('user_profiles')
          .update({ avatar_path: avatarUrl })
          .eq('id', profile.id);
        profile.avatar_path = avatarUrl;
      } catch {
        // Không block luồng chính nếu upload ảnh thất bại
        console.warn('[UserNewService] Avatar upload failed, profile still created.');
      }
    }

    return profile;
  },
};
